import React, { useState, useEffect, useCallback } from 'react';
import { databaseMonitoringAPI, handleApiError } from '../services/api';
import StatCard, { StatGrid, StatTone } from '../components/StatCard';
import './DatabaseMonitoring.css';

const POOL_TONE: Record<string, StatTone> = { healthy: 'success', warning: 'warning', critical: 'error' };

type TabType = 'overview' | 'bottlenecks' | 'config';
type HealthStatus = 'healthy' | 'warning' | 'critical';
type SeverityLevel = 'critical' | 'warning' | 'info';

interface PoolHealth {
  pool_health?: {
    status?: HealthStatus;
    utilization_percentage?: number;
    current_usage?: number;
    available_connections?: number;
    total_capacity?: number;
  };
  recommendations?: string[];
}

interface Bottleneck {
  type: string;
  severity: SeverityLevel;
  message: string;
  recommendation: string;
}

interface BottleneckAnalysis {
  bottlenecks?: Bottleneck[];
  recommendations?: string[];
}

interface BlacklistCache {
  cache_hit_rate?: string;
  cache_size?: number;
  total_checks?: number;
  redis_calls_saved?: number;
}

interface SystemMetrics {
  cpu_usage_percent?: number;
  memory_usage_percent?: number;
  memory_available_gb?: number;
}

interface DatabaseMetrics {
  total_connections?: number;
  active_queries?: number;
  idle_connections?: number;
  longest_query_seconds?: number;
}

interface LoadTestMetrics {
  blacklist_cache?: BlacklistCache;
  system?: SystemMetrics;
  database?: DatabaseMetrics;
}

interface PoolConfiguration {
  pool_size?: number;
  max_overflow?: number;
  pool_timeout?: number;
  pool_recycle?: number;
  pool_pre_ping?: boolean;
}

interface Timeouts {
  statement_timeout?: number;
  query_timeout?: number;
}

interface Recommendations {
  recommended_pool_size?: number;
  recommended_max_overflow?: number;
}

interface Config {
  pool_configuration?: PoolConfiguration;
  timeouts?: Timeouts;
  recommendations?: Recommendations;
}

const DatabaseMonitoring: React.FC = () => {
  const [poolHealth, setPoolHealth] = useState<PoolHealth | null>(null);
  const [bottleneckAnalysis, setBottleneckAnalysis] = useState<BottleneckAnalysis | null>(null);
  const [loadTestMetrics, setLoadTestMetrics] = useState<LoadTestMetrics | null>(null);
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Helper function to safely format numbers
  const formatNumber = (value: number | null | undefined, decimals: number = 2): string => {
    if (value === null || value === undefined || typeof value !== 'number') {
      return '0';
    }
    return value.toFixed(decimals);
  };

  const loadAllData = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [health, bottleneck, metrics, configData] = await Promise.all([
        databaseMonitoringAPI.getPoolHealth(),
        databaseMonitoringAPI.getBottleneckAnalysis(),
        databaseMonitoringAPI.getLoadTestMetrics(),
        databaseMonitoringAPI.getConfig()
      ]);

      // API may return data directly or wrapped in a data property
      const healthData = health.data as PoolHealth & { data?: PoolHealth };
      const bottleneckData = bottleneck.data as BottleneckAnalysis & { data?: BottleneckAnalysis };
      const metricsData = metrics.data as LoadTestMetrics & { data?: LoadTestMetrics };
      const configResponseData = configData.data as Config & { data?: Config };

      setPoolHealth(healthData.data || healthData);
      setBottleneckAnalysis(bottleneckData.data || bottleneckData);
      setLoadTestMetrics(metricsData.data || metricsData);
      setConfig(configResponseData.data || configResponseData);
      setLastUpdated(new Date());
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string | Array<{ msg?: string }> } } };
      const errorMessage = error.response?.data?.detail
        ? (Array.isArray(error.response.data.detail)
          ? error.response.data.detail.map((e) => e.msg).join(', ')
          : error.response.data.detail)
        : handleApiError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadAllData, 30000);
    return () => clearInterval(interval);
  }, [loadAllData]);

  const handleClearCache = async (): Promise<void> => {
    if (window.confirm('Сигурни ли сте, че искате да изчистите blacklist cache?')) {
      try {
        await databaseMonitoringAPI.clearBlacklistCache();
        alert('Cache беше успешно изчистен.');
        loadAllData();
      } catch (err) {
        alert('Грешка при изчистването на cache: ' + handleApiError(err));
      }
    }
  };

  const getHealthBadgeClass = (status: string | undefined): string => {
    switch (status?.toLowerCase()) {
      case 'healthy': return 'badge-success';
      case 'warning': return 'badge-warning';
      case 'critical': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const getSeverityBadgeClass = (severity: string | undefined): string => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'badge-danger';
      case 'warning': return 'badge-warning';
      default: return 'badge-info';
    }
  };

  if (loading && !poolHealth) {
    return (
      <div className="database-monitoring">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Зареждане на данни...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="database-monitoring">
        <div className="error-container">
          <i className="fas fa-exclamation-triangle"></i>
          <h3>Грешка при зареждане на данните</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadAllData}>
            <i className="fas fa-redo"></i> Опитайте отново
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="database-monitoring">
      <div className="monitoring-header">
        <div className="header-left">
          <h2><i className="fas fa-database"></i> Database Monitoring</h2>
          <p className="last-updated">
            Последна актуализация: {lastUpdated.toLocaleTimeString('bg-BG')}
          </p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={loadAllData} disabled={loading}>
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
            Обнови
          </button>
          <button className="btn btn-warning" onClick={handleClearCache}>
            <i className="fas fa-trash"></i>
            Изчисти Cache
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <i className="fas fa-tachometer-alt"></i> Общ преглед
        </button>
        <button
          className={`tab-btn ${activeTab === 'bottlenecks' ? 'active' : ''}`}
          onClick={() => setActiveTab('bottlenecks')}
        >
          <i className="fas fa-exclamation-triangle"></i> Bottlenecks
        </button>
        <button
          className={`tab-btn ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >
          <i className="fas fa-cog"></i> Конфигурация
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="tab-content">
          {/* Health Summary Cards */}
          <StatGrid wide>
            <StatCard
              label="Pool Health"
              icon="fas fa-database"
              tone={POOL_TONE[poolHealth?.pool_health?.status?.toLowerCase() ?? ''] ?? 'neutral'}
              badge={
                <span className={`badge ${getHealthBadgeClass(poolHealth?.pool_health?.status)}`}>
                  {poolHealth?.pool_health?.status || 'unknown'}
                </span>
              }
              value={`${formatNumber(poolHealth?.pool_health?.utilization_percentage, 1)}%`}
              hint="Използване на pool (един API worker)"
              details={[
                { label: 'Активни връзки', value: poolHealth?.pool_health?.current_usage || 0 },
                { label: 'Налични връзки', value: poolHealth?.pool_health?.available_connections || 0 },
                { label: 'Общ капацитет', value: poolHealth?.pool_health?.total_capacity || 0 },
              ]}
            />
            <StatCard
              label="Blacklist Cache"
              icon="fas fa-ban"
              badge={<span className="badge badge-info">Cache Hit Rate</span>}
              value={loadTestMetrics?.blacklist_cache?.cache_hit_rate || '0%'}
              hint="Hit Rate"
              details={[
                { label: 'Cache Size', value: loadTestMetrics?.blacklist_cache?.cache_size || 0 },
                { label: 'Total Checks', value: loadTestMetrics?.blacklist_cache?.total_checks || 0 },
                { label: 'Redis Calls Saved', value: loadTestMetrics?.blacklist_cache?.redis_calls_saved || 0 },
              ]}
            />
            <StatCard
              label="System Resources"
              icon="fas fa-microchip"
              badge={<span className="badge badge-secondary">Live</span>}
              details={[
                { label: 'CPU Usage', value: `${formatNumber(loadTestMetrics?.system?.cpu_usage_percent, 1)}%` },
                { label: 'Memory Usage', value: `${formatNumber(loadTestMetrics?.system?.memory_usage_percent, 1)}%` },
                { label: 'Memory Available', value: `${formatNumber(loadTestMetrics?.system?.memory_available_gb, 2)} GB` },
              ]}
            />
            <StatCard
              label="Database"
              icon="fas fa-server"
              badge={<span className="badge badge-info">Connections</span>}
              details={[
                { label: 'Total Connections', value: loadTestMetrics?.database?.total_connections || 0 },
                { label: 'Active Queries', value: loadTestMetrics?.database?.active_queries || 0 },
                { label: 'Idle Connections', value: loadTestMetrics?.database?.idle_connections || 0 },
                { label: 'Longest Query', value: `${formatNumber(loadTestMetrics?.database?.longest_query_seconds, 2)}s` },
              ]}
            />
          </StatGrid>

          {/* Recommendations */}
          {poolHealth?.recommendations && poolHealth.recommendations.length > 0 && (
            <div className="recommendations-section">
              <h3><i className="fas fa-lightbulb"></i> Препоръки</h3>
              <ul className="recommendations-list">
                {poolHealth.recommendations.map((rec, index) => (
                  <li key={index}>{rec}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Bottlenecks Tab */}
      {activeTab === 'bottlenecks' && (
        <div className="tab-content">
          {bottleneckAnalysis?.bottlenecks && bottleneckAnalysis.bottlenecks.length > 0 ? (
            <div className="bottlenecks-section">
              <h3><i className="fas fa-exclamation-triangle"></i> Открити Bottlenecks</h3>
              <div className="bottlenecks-list">
                {bottleneckAnalysis.bottlenecks.map((bottleneck, index) => (
                  <div key={index} className={`bottleneck-card severity-${bottleneck.severity}`}>
                    <div className="bottleneck-header">
                      <span className={`badge ${getSeverityBadgeClass(bottleneck.severity)}`}>
                        {bottleneck.severity}
                      </span>
                      <span className="bottleneck-type">{bottleneck.type}</span>
                    </div>
                    <div className="bottleneck-message">{bottleneck.message}</div>
                    <div className="bottleneck-recommendation">
                      <i className="fas fa-arrow-right"></i> {bottleneck.recommendation}
                    </div>
                  </div>
                ))}
              </div>

              {bottleneckAnalysis.recommendations && (
                <div className="recommendations-section">
                  <h3><i className="fas fa-lightbulb"></i> Общи Препоръки</h3>
                  <ul className="recommendations-list">
                    {bottleneckAnalysis.recommendations.map((rec, index) => (
                      <li key={index}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="no-bottlenecks">
              <i className="fas fa-check-circle"></i>
              <p>Няма открити bottlenecks. Системата работи нормално.</p>
            </div>
          )}
        </div>
      )}

      {/* Configuration Tab */}
      {activeTab === 'config' && config && (
        <div className="tab-content">
          <div className="config-section">
            <h3><i className="fas fa-cog"></i> Pool Configuration</h3>
            <div className="config-grid">
              <div className="config-item">
                <span className="config-label">Pool Size:</span>
                <span className="config-value">{config.pool_configuration?.pool_size}</span>
              </div>
              <div className="config-item">
                <span className="config-label">Max Overflow:</span>
                <span className="config-value">{config.pool_configuration?.max_overflow}</span>
              </div>
              <div className="config-item">
                <span className="config-label">Pool Timeout:</span>
                <span className="config-value">{config.pool_configuration?.pool_timeout}s</span>
              </div>
              <div className="config-item">
                <span className="config-label">Pool Recycle:</span>
                <span className="config-value">{config.pool_configuration?.pool_recycle}s</span>
              </div>
              <div className="config-item">
                <span className="config-label">Pool Pre-Ping:</span>
                <span className="config-value">{config.pool_configuration?.pool_pre_ping ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>

          <div className="config-section">
            <h3><i className="fas fa-clock"></i> Timeouts</h3>
            <div className="config-grid">
              <div className="config-item">
                <span className="config-label">Statement Timeout:</span>
                <span className="config-value">{config.timeouts?.statement_timeout}s</span>
              </div>
              <div className="config-item">
                <span className="config-label">Query Timeout:</span>
                <span className="config-value">{config.timeouts?.query_timeout}s</span>
              </div>
            </div>
          </div>

          <div className="config-section">
            <h3><i className="fas fa-chart-line"></i> Recommendations</h3>
            <div className="config-grid">
              <div className="config-item">
                <span className="config-label">Recommended Pool Size:</span>
                <span className="config-value">{config.recommendations?.recommended_pool_size}</span>
              </div>
              <div className="config-item">
                <span className="config-label">Recommended Max Overflow:</span>
                <span className="config-value">{config.recommendations?.recommended_max_overflow}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseMonitoring;
